/**
 * The archive (brief §content: `/library/exams`). Three sections, all of them
 * "here is the whole paper, with solutions":
 *
 *   1. **Gamla tentor / Past exams** — the real papers (HT22 … HT25).
 *   2. **Övningstentor / Practice papers** — PE-01 …, newly written from the
 *      course test bank. Explicitly labelled as training material, not
 *      official papers, and served verified-only: while the import is still
 *      in draft the section renders its empty state rather than disappearing,
 *      so the student can tell "not written yet" from "not there".
 *   3. **Hemuppgifter / Hand-ins** — the course's weekly bonus hand-ins, one
 *      section per term, grouped by hand-in number.
 *
 * Every problem shows its statement, given data and figures immediately;
 * "Visa lösning / Show solution" reveals the solution, rubric and final answer
 * (the archive routes return them straight away — spec §4.1: "old exams (full,
 * logged-in only)" — so there is no grading step here, just a reveal toggle).
 * "Öva liknande / Practice similar" preselects the problem's topic on
 * `/practice`; "Skriv som övningstenta / Sit as a practice exam" starts the paper as a
 * real, timed exam session (`/exam?past=<term>` or `/exam?paper=<PE-nn>`).
 */
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Figure } from "@client/components/Figure";
import { Localized } from "@client/components/Localized";
import { QuestionParts } from "@client/components/QuestionParts";
import { ReportIssue } from "@client/components/ReportIssue";
import { SourceBadge, TranslationPendingBadge } from "@client/components/SourceBadge";
import { Badge, Button, Card, EmptyState, PageHeader, Spinner } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDate, formatPoints, formatScientific } from "@client/lib/format";
import { messageForError } from "@client/lib/errors";
import {
  useHomeworkDetails,
  useHomeworkList,
  useOldExamDetails,
  useOldExamsList,
  usePracticePaperDetails,
  usePracticePapersList,
} from "./api";
import { practiceHref, sitOldExamHref, sitPracticePaperHref } from "./links";
import type { OldExamProblemDto } from "./types";

/** Stable anchor ids the "Innehåll / Contents" jump-list and the "Till
 * början / Back to top" links target — built once here so every id used in
 * an `href` is guaranteed to match the id on the heading it points to. */
const examHeadingId = (exam: string) => `exam-${exam.toLowerCase()}`;
const paperHeadingId = (paper: string) => `paper-${paper.toLowerCase()}`;
const homeworkSectionId = (term: string) => `section-homework-${term.toLowerCase()}`;
const homeworkHeadingId = (term: string, number: number) =>
  `homework-${term.toLowerCase()}-${number}`;
const TOP_ID = "exams-top";

/** Moves focus to the jump target after the browser follows the `href`, so
 * screen-reader users land on the heading rather than just its text — not
 * every browser moves focus to a `tabIndex={-1}` target on hash navigation. */
function focusHeading(id: string) {
  return () => {
    document.getElementById(id)?.focus();
  };
}

function ProblemCard({ problem }: { problem: OldExamProblemDto }) {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const { question } = problem;

  return (
    <Card compact className="mb-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-ink-800 dark:text-ink-100">
          {t("content.exams.problemTitle", { number: problem.number })}
        </p>
        <span className="flex flex-wrap items-center gap-2">
          <SourceBadge source={problem.source} />
          <TranslationPendingBadge pending={problem.translationPending} />
          <Badge tone="neutral">{formatPoints(problem.points, lang)}</Badge>
        </span>
      </div>

      <Localized value={question.statement} />
      {question.given.map((item, index) => (
        <Localized key={index} value={item} variant="compact" />
      ))}
      {question.figures
        .filter((figure) => !figure.partId)
        .map((figure, index) => (
          <Figure key={index} figure={figure} />
        ))}
      {/* An archived exam problem usually states its sub-questions in
          `parts[].prompt`; without this the card shows only the lead-in. */}
      {question.type !== "mc" && (
        <QuestionParts parts={question.parts} figures={question.figures} />
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 no-print">
        <Button variant="secondary" size="sm" onClick={() => setRevealed((v) => !v)}>
          {revealed ? t("content.exams.hide") : t("content.exams.reveal")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(practiceHref(problem.chapter, problem.topic))}
        >
          {t("content.exams.practiceSimilar")}
        </Button>
        <ReportIssue questionId={question.id} />
      </div>

      {revealed && (
        <div className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800">
          <Localized value={question.solution} />
          {question.solutionFigure && <Figure figure={question.solutionFigure} />}

          {question.type === "mc" && (
            <ul className="mt-3 space-y-1 text-sm">
              {question.options.map((option) => (
                <li
                  key={option.id}
                  className={
                    option.correct
                      ? "font-medium text-green-700 dark:text-green-300"
                      : "text-ink-600 dark:text-ink-400"
                  }
                >
                  {option.correct ? "✓ " : ""}
                  <Localized value={option.text} variant="inline" />
                </li>
              ))}
            </ul>
          )}

          {question.type === "numeric" && (
            <ul className="mt-3 space-y-1 text-sm text-ink-700 dark:text-ink-200">
              {question.parts.map((part) => (
                <li key={part.id}>
                  {formatScientific(part.answer, lang)} {part.unit}
                </li>
              ))}
            </ul>
          )}

          {question.type === "open" && (
            <>
              {question.rubric.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
                    {t("content.exams.rubric")}
                  </p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-ink-700 dark:text-ink-200">
                    {question.rubric.map((item) => (
                      <li key={item.id}>
                        <Localized value={item.text} variant="inline" /> —{" "}
                        {formatPoints(item.points, lang)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {question.finalAnswer && (
                <div className="mt-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
                    {t("content.exams.finalAnswer")}
                  </p>
                  <Localized value={question.finalAnswer} variant="inline" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/** One paper: a heading line (title + optional note) with an optional action, then its problems.
 * `id` is the paper's stable anchor (spec: archive contents jump-list); the
 * heading takes `scroll-mt-20` so the sticky app header doesn't cover it and
 * `tabIndex={-1}` so a jump link can focus it (see `focusHeading`). */
function PaperBlock({
  id,
  title,
  note,
  action,
  problems,
}: {
  id: string;
  title: ReactNode;
  note?: string;
  action?: ReactNode;
  problems: OldExamProblemDto[];
}) {
  const t = useT();
  return (
    <div className="mb-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3
          id={id}
          tabIndex={-1}
          className="scroll-mt-20 font-display text-lg text-ink-900 outline-none dark:text-ink-50"
        >
          {title}
        </h3>
        <span className="flex flex-wrap items-center gap-3 no-print">
          <a
            href={`#${TOP_ID}`}
            onClick={focusHeading(TOP_ID)}
            className="text-xs text-ink-500 underline underline-offset-2 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100"
          >
            {t("content.exams.contents.backToTop")}
          </a>
          {action}
        </span>
      </div>
      {note && <p className="mb-3 text-sm text-ink-600 dark:text-ink-400">{note}</p>}
      {problems.map((problem) => (
        <ProblemCard key={problem.question.id} problem={problem} />
      ))}
    </div>
  );
}

/** `id` is the section's stable anchor for the contents jump-list; see `PaperBlock` above. */
function Section({ id, heading, children }: { id: string; heading: string; children: ReactNode }) {
  return (
    <section className="mb-10">
      <h2
        id={id}
        tabIndex={-1}
        className="mb-4 scroll-mt-20 font-display text-xl text-ink-900 outline-none dark:text-ink-50"
      >
        {heading}
      </h2>
      {children}
    </section>
  );
}

export function ExamsPage() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();

  const examsQuery = useOldExamsList();
  const exams = examsQuery.data?.exams ?? [];
  const examDetails = useOldExamDetails(exams.map((entry) => entry.exam));

  const papersQuery = usePracticePapersList();
  const papers = papersQuery.data?.papers ?? [];
  const paperDetails = usePracticePaperDetails(papers.map((entry) => entry.paper));

  const homeworkQuery = useHomeworkList();
  const terms = homeworkQuery.data?.terms ?? [];
  const homeworkDetails = useHomeworkDetails(terms.map((entry) => entry.term));

  const listsLoading = examsQuery.isLoading || papersQuery.isLoading || homeworkQuery.isLoading;
  const detailsLoading = [...examDetails, ...paperDetails, ...homeworkDetails].some(
    (q) => q.isLoading,
  );
  const listError = examsQuery.error ?? papersQuery.error ?? homeworkQuery.error;

  // The "Innehåll / Contents" jump-list mirrors exactly what's on the page:
  // a paper only gets a link once its detail has resolved (matching the
  // `if (!detail) return null` guards below), and a section is omitted
  // entirely once it has no papers to link to — its empty state still
  // renders further down, this list just isn't cluttered with a dead entry
  // for it. Homework gets one contents section per term, since the page
  // itself renders one `<Section>` per term rather than a single one.
  const pastExamsSection = {
    id: "section-past-exams",
    heading: t("content.exams.pastExams"),
    papers: exams.flatMap((entry, index) => {
      const detail = examDetails[index]?.data;
      if (!detail) return [];
      const dateLabel =
        entry.date !== null ? formatDate(entry.date, lang) : t("content.exams.dateUnknown");
      return [{ id: examHeadingId(entry.exam), label: `${entry.exam} · ${dateLabel}` }];
    }),
  };
  const practicePapersSection = {
    id: "section-practice-papers",
    heading: t("content.exams.practicePapers"),
    papers: papers.flatMap((entry, index) => {
      const detail = paperDetails[index]?.data;
      if (!detail) return [];
      return [{ id: paperHeadingId(entry.paper), label: entry.paper }];
    }),
  };
  const homeworkSections = terms.flatMap((entry, index) => {
    const detail = homeworkDetails[index]?.data;
    if (!detail) return [];
    return [
      {
        id: homeworkSectionId(entry.term),
        heading: t("content.exams.homework.heading", { term: entry.term }),
        papers: detail.assignments.map((assignment) => ({
          id: homeworkHeadingId(entry.term, assignment.number),
          label: t("content.exams.homework.assignment", { number: assignment.number }),
        })),
      },
    ];
  });
  const contentsSections = [pastExamsSection, practicePapersSection, ...homeworkSections].filter(
    (section) => section.papers.length > 0,
  );

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={
          <span id={TOP_ID} tabIndex={-1} className="outline-none">
            {t("content.exams.title")}
          </span>
        }
        description={t("content.exams.description")}
      />

      {contentsSections.length > 0 && (
        <nav aria-labelledby="exams-contents-label" className="no-print mb-8">
          <p
            id="exams-contents-label"
            className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400"
          >
            {t("content.exams.contents.title")}
          </p>
          <ul className="space-y-2 text-sm">
            {contentsSections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  onClick={focusHeading(section.id)}
                  className="font-medium text-accent-700 underline underline-offset-2 hover:text-accent-900 dark:text-accent-300 dark:hover:text-accent-100"
                >
                  {section.heading}
                </a>
                <ul className="ml-4 mt-1 space-y-1">
                  {section.papers.map((paper) => (
                    <li key={paper.id}>
                      <a
                        href={`#${paper.id}`}
                        onClick={focusHeading(paper.id)}
                        className="text-ink-600 underline underline-offset-2 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100"
                      >
                        {paper.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {listError && <EmptyState title={messageForError(listError, t)} />}

      {(listsLoading || detailsLoading) && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" label={t("common.loading")} />
        </div>
      )}

      <Section id="section-past-exams" heading={t("content.exams.pastExams")}>
        {examsQuery.isSuccess && exams.length === 0 && (
          <EmptyState title={t("content.exams.empty")} />
        )}
        {exams.map((entry, index) => {
          const detail = examDetails[index]?.data;
          if (!detail) return null;
          return (
            <PaperBlock
              key={entry.exam}
              id={examHeadingId(entry.exam)}
              title={
                <span className="flex flex-wrap items-baseline gap-x-2">
                  {entry.exam}
                  {/* The separator is real text, not a margin: the heading is
                      read (and copied, and matched in tests) as one string,
                      and "HT22" ran straight into "12 jan. 2023" without it. */}
                  <span className="text-sm font-normal text-ink-500 dark:text-ink-400">
                    ·{" "}
                    {entry.date !== null
                      ? formatDate(entry.date, lang)
                      : t("content.exams.dateUnknown")}
                  </span>
                </span>
              }
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  className="no-print"
                  onClick={() => navigate(sitOldExamHref(entry.exam))}
                >
                  {t("content.exams.sitThis")}
                </Button>
              }
              problems={detail.problems}
            />
          );
        })}
      </Section>

      <Section id="section-practice-papers" heading={t("content.exams.practicePapers")}>
        <p className="mb-4 text-sm text-ink-600 dark:text-ink-400">
          {t("content.exams.practicePapers.note")}
        </p>
        {papersQuery.isSuccess && papers.length === 0 && (
          <EmptyState title={t("content.exams.practicePapers.empty")} />
        )}
        {papers.map((entry, index) => {
          const detail = paperDetails[index]?.data;
          if (!detail) return null;
          return (
            <PaperBlock
              key={entry.paper}
              id={paperHeadingId(entry.paper)}
              title={entry.paper}
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  className="no-print"
                  onClick={() => navigate(sitPracticePaperHref(entry.paper))}
                >
                  {t("content.exams.sitThis")}
                </Button>
              }
              problems={detail.problems}
            />
          );
        })}
      </Section>

      {/* One section per term; before the list resolves (and when it is empty)
          the generic heading stands in, so the page structure never jumps. */}
      {terms.length === 0 && (
        <Section id="section-homework" heading={t("content.exams.homework.title")}>
          {homeworkQuery.isSuccess && <EmptyState title={t("content.exams.homework.empty")} />}
        </Section>
      )}

      {terms.map((entry, index) => {
        const detail = homeworkDetails[index]?.data;
        if (!detail) return null;
        return (
          <Section
            key={entry.term}
            id={homeworkSectionId(entry.term)}
            heading={t("content.exams.homework.heading", { term: entry.term })}
          >
            {detail.assignments.map((assignment) => (
              <PaperBlock
                key={assignment.number}
                id={homeworkHeadingId(entry.term, assignment.number)}
                title={t("content.exams.homework.assignment", { number: assignment.number })}
                problems={assignment.problems}
              />
            ))}
          </Section>
        );
      })}
    </div>
  );
}

export default ExamsPage;
